<script setup>
import { computed } from 'vue';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Flag from 'dashboard/components-next/flag/Flag.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import { dateFormat } from 'shared/helpers/timeHelper';
import countries from 'shared/constants/countries';

const props = defineProps({
  contact: { type: Object, required: true },
  columns: { type: Array, required: true },
  isSelected: { type: Boolean, default: false },
});

const emit = defineEmits(['toggleSelect', 'showContact']);

const countriesMap = countries.reduce((acc, country) => {
  acc[country.code] = country;
  acc[country.id] = country;
  return acc;
}, {});

const companyLabel = computed(
  () =>
    props.contact.empresa?.name ||
    props.contact.additionalAttributes?.companyName ||
    ''
);

const countryDetails = computed(() => {
  const { country, countryCode, city } = props.contact.additionalAttributes || {};
  if (!country && !countryCode) return null;

  const activeCountry = countriesMap[country] || countriesMap[countryCode];
  if (!activeCountry) return null;

  return {
    countryCode: activeCountry.id,
    name: [city ? `${city},` : null, activeCountry.name].filter(Boolean).join(' '),
  };
});

const customValue = column => {
  const raw = props.contact.customAttributes?.[column.attributeKey];
  if (raw === undefined || raw === null || raw === '') return '';
  if (column.type === 'checkbox') {
    return raw === true || raw === 'true' ? '✓' : '—';
  }
  return raw;
};

const textValue = column => {
  switch (column.type) {
    case 'email':
      return props.contact.email;
    case 'phone':
      return props.contact.phoneNumber;
    case 'city':
      return props.contact.additionalAttributes?.city;
    case 'date':
      return props.contact.createdAt ? dateFormat(props.contact.createdAt) : '';
    default:
      return '';
  }
};

const onSelect = event => emit('toggleSelect', event.target.checked);
const onShow = () => emit('showContact');
</script>

<template>
  <tr
    class="h-11 cursor-pointer hover:bg-n-alpha-1"
    :class="{ 'bg-n-slate-3 dark:bg-n-solid-3': isSelected }"
    @click="onShow"
  >
    <td class="w-10 px-3" @click.stop>
      <Checkbox :model-value="isSelected" @change="onSelect" />
    </td>
    <td
      v-for="column in columns"
      :key="column.key"
      class="px-3 py-1.5 text-sm text-n-slate-11 max-w-xs"
    >
      <!-- Name + avatar -->
      <div v-if="column.type === 'name'" class="flex items-center gap-2 min-w-0">
        <Avatar
          :name="contact.name"
          :src="contact.thumbnail"
          :size="24"
          :status="contact.availabilityStatus"
          :blocked="contact.blocked"
          hide-offline-status
          rounded-full
        />
        <button
          type="button"
          class="font-medium truncate text-n-slate-12 hover:underline"
          @click.stop="onShow"
        >
          {{ contact.name }}
        </button>
      </div>

      <!-- Company -->
      <span v-else-if="column.type === 'company'" class="truncate" :title="companyLabel">
        {{ companyLabel || '—' }}
      </span>

      <!-- Country (+ city) with flag -->
      <span
        v-else-if="column.type === 'country'"
        class="inline-flex items-center gap-1.5 truncate"
      >
        <template v-if="countryDetails">
          <Flag :country="countryDetails.countryCode" class="size-3.5 shrink-0" />
          <span class="truncate">{{ countryDetails.name }}</span>
        </template>
        <span v-else>—</span>
      </span>

      <!-- Custom attributes -->
      <span
        v-else-if="column.isCustom"
        class="truncate"
        :title="customValue(column)"
      >
        {{ customValue(column) || '—' }}
      </span>

      <!-- Built-in text columns (email / phone / city / created_at) -->
      <span v-else class="truncate" :title="textValue(column)">
        {{ textValue(column) || '—' }}
      </span>
    </td>
  </tr>
</template>
